import type { BulkConversationActionProgressNotification } from "@redotech/effect-api-shared/models/team/user-notification";
import { toast } from "@redotech/redo-web/alert";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTeamNotificationStream } from "../stream/team-notification-stream-provider";

function getActionLabel(
  action: BulkConversationActionProgressNotification["action"],
): string {
  switch (action._tag) {
    case "setConversationStatus":
      switch (action.status) {
        case "CLOSED":
          return "Closing tickets";
        case "OPEN":
          return "Reopening tickets";
        case "ARCHIVED":
          return "Archiving tickets";
        case "IN_PROGRESS":
          return "Marking tickets in progress";
        default:
          return "Updating tickets";
      }
    case "setReadStatus":
      return action.status === "read"
        ? "Marking tickets read"
        : "Marking tickets unread";
    case "setAssignee":
      return "Assigning tickets";
    case "addTags":
      return "Adding tags to tickets";
    case "removeTags":
      return "Removing tags from tickets";
  }
}

function getCompletionMessage(
  event: BulkConversationActionProgressNotification,
): string {
  const count = event.processedCount;
  const tickets = count === 1 ? "ticket" : "tickets";
  const { action } = event;
  switch (action._tag) {
    case "setConversationStatus":
      switch (action.status) {
        case "CLOSED":
          return `Closed ${count} ${tickets}`;
        case "OPEN":
          return `Reopened ${count} ${tickets}`;
        case "ARCHIVED":
          return `Archived ${count} ${tickets}`;
        case "IN_PROGRESS":
          return `Marked ${count} ${tickets} as in progress`;
        default:
          return `Updated ${count} ${tickets}`;
      }
    case "setReadStatus":
      return action.status === "read"
        ? `Marked ${count} ${tickets} as read`
        : `Marked ${count} ${tickets} as unread`;
    case "setAssignee":
      return `Assigned ${count} ${tickets}`;
    case "addTags":
      return `Added tags to ${count} ${tickets}`;
    case "removeTags":
      return `Removed tags from ${count} ${tickets}`;
  }
}

export const BulkActionProgressBar = memo(function BulkActionProgressBar() {
  const [eventsByWorkflowId, setEventsByWorkflowId] = useState<
    Record<string, BulkConversationActionProgressNotification>
  >({});
  const notifiedWorkflowIds = useRef<Set<string>>(new Set());
  const completionTimeouts = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  useEffect(() => {
    const timeouts = completionTimeouts.current;
    return () => {
      for (const timeout of timeouts.values()) {
        clearTimeout(timeout);
      }
    };
  }, []);

  useTeamNotificationStream("bulkConversationActionProgress", (event) => {
    if (
      (event.status === "completed" || event.status === "failed") &&
      !notifiedWorkflowIds.current.has(event.workflowId)
    ) {
      notifiedWorkflowIds.current.add(event.workflowId);
      if (event.status === "completed") {
        toast(getCompletionMessage(event), { variant: "success" });
      } else {
        toast(
          `Failed to complete bulk action. ${event.processedCount} of ${event.totalCount} processed.`,
          { variant: "error" },
        );
      }
    }

    if (event.status === "completed" || event.status === "failed") {
      const timeout = setTimeout(() => {
        setEventsByWorkflowId((previous) => {
          const next = { ...previous };
          delete next[event.workflowId];
          return next;
        });
        completionTimeouts.current.delete(event.workflowId);
      }, 1500);
      completionTimeouts.current.set(event.workflowId, timeout);
    }

    setEventsByWorkflowId((previous) => ({
      ...previous,
      [event.workflowId]: event,
    }));
  });

  const inProgressEvents = useMemo(
    () =>
      Object.values(eventsByWorkflowId).filter(
        (event) =>
          event.status === "started" ||
          event.status === "processing" ||
          event.status === "completed",
      ),
    [eventsByWorkflowId],
  );

  if (inProgressEvents.length === 0) {
    return null;
  }

  return (
    <Flex dir="column" gap="sm">
      {inProgressEvents.map((event) => {
        const percentage =
          event.totalCount === 0
            ? 0
            : Math.round((event.processedCount / event.totalCount) * 100);

        return (
          <Flex
            bgColor="primary"
            borderColor="secondary"
            borderStyle="solid"
            borderWidth="1px"
            dir="column"
            gap="xs"
            key={event.workflowId}
            p="md"
            radius="md"
          >
            <Text fontSize="sm" fontWeight="medium" textColor="primary">
              {getActionLabel(event.action)}
            </Text>
            <RedoProgressBar
              labelStyle="text"
              percentage={percentage}
              style="thin"
            />
          </Flex>
        );
      })}
    </Flex>
  );
});
